import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  AccessTimeOutlined as AccessTimeIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  DeleteForeverOutlined,
  Edit as EditIcon,
  Link as LinkIcon,
  ManageAccounts as ManageAccountsIcon,
  People,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";

import { useAuth } from "../../context/AuthContext";
import CustomModal from "../../Components/CustomModal";
import CustomSnackBar from "../../Components/CustomSnackBar";
import DatePick from "../../Components/DatePick";
import { Accounts, RBAC } from "../../services/api";
import MainText from "../../Components/MainText";

const MONTHS_SHORT = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];

const WEEKDAYS = [
  { value: 0, label: "Lun" },
  { value: 1, label: "Mar" },
  { value: 2, label: "Mie" },
  { value: 3, label: "Jue" },
  { value: 4, label: "Vie" },
  { value: 5, label: "Sab" },
  { value: 6, label: "Dom" },
];

const DEFAULT_ACCESS_SCHEDULE = {
  enabled: false,
  start_time: "08:00",
  end_time: "18:00",
  allowed_days: [],
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const normalizeCompany = (source) => {
  const item = source?.company || source?.branch || source;
  if (!item || typeof item !== "object") return null;

  return {
    id: item.id,
    name: item.name || item.alias || item.legalName || `Empresa ${item.id}`,
  };
};

const formatAbsolute = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = MONTHS_SHORT[date.getMonth()] || "mes";
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");
  return `${dd} ${mm} ${yyyy}, ${hh}:${mi}`;
};

const formatRelative = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  const diffMs = Date.now() - date.getTime();
  if (diffMs < 0) return formatAbsolute(value);

  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1) return "Hace segundos";
  if (diffMinutes < 60) return `Hace ${diffMinutes} min`;
  if (diffHours < 24) return `Hace ${diffHours} h`;
  if (diffDays === 1) return "Ayer";
  if (diffDays < 7) return `Hace ${diffDays} dias`;
  return formatAbsolute(value).split(",")[0];
};

const formatDateTime = (value) => {
  if (!value) return "Sin fecha";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin fecha";

  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");
  const ss = String(date.getSeconds()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}`;
};

const toQueryDate = (value) => {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString().slice(0, 10);
};

const normalizePermission = (source) => {
  if (!source || typeof source !== "object") return null;
  return {
    ...source,
    friendly_name:
      source.friendly_name ||
      source.label_es ||
      source.name ||
      source.code ||
      `Permiso ${source.id}`,
    caption: source.caption || source.description || "",
    example_es: source.example_es || "",
  };
};

const permissionFriendlyLabel = (permission) =>
  permission?.friendly_name || permission?.label_es || permission?.name || permission?.code || "Permiso";

const permissionCaption = (permission, isSuperuser = false) => {
  const caption = String(permission?.caption || permission?.description || "").trim();
  const example = String(permission?.example_es || "").trim();
  const detail = [caption, example].filter(Boolean).join(" - ");
  const code = String(permission?.code || "").trim();
  if (!isSuperuser) return detail;
  if (code && detail) return `${code} - ${detail}`;
  return code || detail || "";
};

function AssignCompanyRoleForm({
  setOpen,
  targetUser,
  companies = [],
  roles = [],
  assign,
  setAssign,
  onSubmit,
}) {
  const [loading, setLoading] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const filteredRoles = useMemo(() => {
    if (!assign?.company_id) return [];
    return roles.filter((role) => {
      if (!role || !role.id) return false;
      return role.is_active !== false;
    });
  }, [assign?.company_id, roles]);

  const handleSave = async () => {
    if (!assign?.company_id) {
      setSnackMsg("Selecciona una empresa.");
      setSnackSev("warning");
      setSnackOpen(true);
      return;
    }
    if (!assign?.role) {
      setSnackMsg("Selecciona un rol.");
      setSnackSev("warning");
      setSnackOpen(true);
      return;
    }

    try {
      setLoading(true);
      await Promise.resolve(onSubmit?.(assign));
      setSnackMsg("Asignacion guardada.");
      setSnackSev("success");
      setSnackOpen(true);
      setTimeout(() => setOpen(false), 300);
    } catch {
      setSnackMsg("No se pudo guardar la asignacion.");
      setSnackSev("error");
      setSnackOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {targetUser
            ? `${(targetUser.first_name || "").trim()} ${(targetUser.last_name || "").trim()} (${targetUser.email || targetUser.username})`
            : ""}
        </Typography>

        <Divider />

        <FormControl fullWidth size="small">
          <InputLabel>Empresa</InputLabel>
          <Select
            label="Empresa"
            value={assign.company_id || ""}
            onChange={(event) =>
              setAssign((prev) => ({
                ...prev,
                company_id: event.target.value,
                role: "",
              }))
            }
          >
            {companies.map((company) => (
              <MenuItem key={company.id} value={company.id}>
                {company.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" disabled={!assign.company_id}>
          <InputLabel>Rol</InputLabel>
          <Select
            label="Rol"
            value={assign.role || ""}
            onChange={(event) =>
              setAssign((prev) => ({ ...prev, role: event.target.value }))
            }
          >
            {filteredRoles.map((role) => (
              <MenuItem key={role.id} value={role.id}>
                {role.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(assign.makeCurrent)}
              onChange={(event) =>
                setAssign((prev) => ({ ...prev, makeCurrent: event.target.checked }))
              }
            />
          }
          label="Marcar como activa (current)"
        />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button disabled={loading} onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={loading || !(assign.company_id && assign.role)}
            onClick={handleSave}
          >
            {loading ? "Guardando..." : "Guardar"}
          </Button>
        </Stack>
      </Stack>

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />
    </>
  );
}

export default function UsersAdminPage() {
  const isMobile = useMediaQuery("(max-width:768px)");
  const { isSuperuser, permissions = [] } = useAuth();

  const canManageUsers = isSuperuser || permissions.includes("manage_users");
  const canAssignUsers =
    isSuperuser || permissions.includes("manage_users") || permissions.includes("manage_roles");
  const canAssignExtras =
    isSuperuser || permissions.includes("assign_extra_permissions");

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [roles, setRoles] = useState([]);

  const [assignOpen, setAssignOpen] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  const [assign, setAssign] = useState({ company_id: "", role: "", makeCurrent: false });

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [activeOnly, setActiveOnly] = useState(true);
  const [companyId, setCompanyId] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null, loading: false });
  const [accessScheduleDialog, setAccessScheduleDialog] = useState({
    open: false,
    user: null,
    loading: false,
    saving: false,
    form: { ...DEFAULT_ACCESS_SCHEDULE },
  });

  const [extraPanelOpen, setExtraPanelOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [availablePerms, setAvailablePerms] = useState([]);
  const [extraAssignments, setExtraAssignments] = useState([]);
  const [extraCompanyId, setExtraCompanyId] = useState("");
  const [extraSelectedIds, setExtraSelectedIds] = useState([]);
  const [extraLoading, setExtraLoading] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);


  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timeout);
  }, [search]);

  const showSnack = (message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  };

  const loadCompanies = useCallback(async () => {
    try {
      let data = [];
      if (isSuperuser) {
        data = await RBAC.companies.list();
      } else {
        try {
          data = await RBAC.companiesMine();
        } catch {
          data = await RBAC.userCompanies("me");
        }
      }
      const normalized = toArray(data).map(normalizeCompany).filter(Boolean);
      setCompanies(normalized);
    } catch {
      setCompanies([]);
      showSnack("No se pudieron cargar las empresas.", "error");
    }
  }, [isSuperuser]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        search: debouncedSearch || undefined,
        company_id: companyId || undefined,
        start_date: toQueryDate(startDate),
        end_date: toQueryDate(endDate),
        active: activeOnly ? "true" : "false",
      };
      const data = await Accounts.users(params);
      setUsers(toArray(data));
    } catch {
      setUsers([]);
      showSnack("No se pudieron cargar los usuarios.", "error");
    } finally {
      setLoading(false);
    }
  }, [activeOnly, companyId, debouncedSearch, endDate, startDate]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (!canAssignExtras || !extraPanelOpen) return;

    (async () => {
      try {
        const data = await RBAC.permissions();
        setAvailablePerms(
          toArray(data)
            .map(normalizePermission)
            .filter(Boolean)
            .sort((left, right) =>
              String(permissionFriendlyLabel(left)).localeCompare(String(permissionFriendlyLabel(right)))
            )
        );
      } catch {
        setAvailablePerms([]);
      }
    })();
  }, [canAssignExtras, extraPanelOpen]);

  const loadRolesByCompany = useCallback(async (nextCompanyId) => {
    if (!nextCompanyId) {
      setRoles([]);
      return;
    }
    try {
      const data = await RBAC.rolesByCompany(nextCompanyId);
      setRoles(toArray(data));
    } catch {
      setRoles([]);
    }
  }, []);

  useEffect(() => {
    if (assign.company_id) {
      loadRolesByCompany(assign.company_id);
    }
  }, [assign.company_id, loadRolesByCompany]);

  const openAssign = useCallback((userRow, preferredCompanyId = "") => {
    setTargetUser(userRow);
    const userCompanies = toArray(userRow?.user_companies);
    const current =
      userCompanies.find((item) => item.current) ||
      userCompanies.find((item) => String(item.company?.id) === String(preferredCompanyId)) ||
      userCompanies[0];

    setAssign({
      company_id: preferredCompanyId || current?.company?.id || "",
      role: current?.role?.id || "",
      makeCurrent: Boolean(current?.current),
    });
    setAssignOpen(true);
  }, []);

  const handleAssign = async (payload) => {
    if (!targetUser?.id || !payload.company_id || !payload.role) return;
    await RBAC.assignUserCompany({
      user_id: targetUser.id,
      company_id: payload.company_id,
      role: payload.role,
      is_active: true,
      current: Boolean(payload.makeCurrent),
      extra_permission_ids: [],
    });
    showSnack("Asignacion guardada correctamente.", "success");
    setAssignOpen(false);
    await loadUsers();
  };

  const handleToggleUser = useCallback(async (row) => {
    try {
      await Accounts.setUserActive(row.id, !row.is_active);
      showSnack(row.is_active ? "Usuario desactivado." : "Usuario activado.", "success");
      await loadUsers();
    } catch (error) {
      showSnack(
        error?.response?.data?.detail || "No se pudo actualizar el estado del usuario.",
        "error"
      );
    }
  }, [loadUsers]);

  const handleDeleteUser = useCallback(async () => {
    const user = deleteDialog.user;
    if (!user?.id) return;

    setDeleteDialog((prev) => ({ ...prev, loading: true }));
    try {
      await Accounts.deleteUser(user.id);
      setDeleteDialog({ open: false, user: null, loading: false });
      setSelectedUser((prev) => (String(prev?.id || "") === String(user.id) ? null : prev));
      setSelectionModel((prev) => prev.filter((rowId) => String(rowId) !== String(user.id)));
      showSnack("Usuario eliminado permanentemente.", "success");
      await loadUsers();
    } catch (error) {
      setDeleteDialog((prev) => ({ ...prev, loading: false }));
      showSnack(
        error?.response?.data?.detail || "No se pudo eliminar permanentemente el usuario.",
        "error"
      );
    }
  }, [deleteDialog.user, loadUsers]);

  const openAccessSchedule = useCallback(async (userRow) => {
    if (!userRow?.id) return;
    setAccessScheduleDialog({
      open: true,
      user: userRow,
      loading: true,
      saving: false,
      form: { ...DEFAULT_ACCESS_SCHEDULE },
    });
    try {
      const data = await Accounts.getUserAccessSchedule(userRow.id);
      setAccessScheduleDialog((prev) => ({
        ...prev,
        loading: false,
        form: {
          enabled: Boolean(data?.enabled),
          start_time: data?.start_time || DEFAULT_ACCESS_SCHEDULE.start_time,
          end_time: data?.end_time || DEFAULT_ACCESS_SCHEDULE.end_time,
          allowed_days: Array.isArray(data?.allowed_days) ? data.allowed_days : [],
        },
      }));
    } catch (error) {
      setAccessScheduleDialog((prev) => ({ ...prev, loading: false }));
      showSnack(
        error?.response?.data?.detail || "No se pudo cargar el horario de acceso.",
        "error"
      );
    }
  }, []);

  const closeAccessSchedule = useCallback(() => {
    setAccessScheduleDialog((prev) => {
      if (prev.saving) return prev;
      return {
        open: false,
        user: null,
        loading: false,
        saving: false,
        form: { ...DEFAULT_ACCESS_SCHEDULE },
      };
    });
  }, []);

  const updateAccessScheduleForm = useCallback((patch) => {
    setAccessScheduleDialog((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        ...patch,
      },
    }));
  }, []);

  const toggleAccessScheduleDay = useCallback((dayValue) => {
    setAccessScheduleDialog((prev) => {
      const currentDays = Array.isArray(prev.form.allowed_days) ? prev.form.allowed_days : [];
      const nextDays = currentDays.includes(dayValue)
        ? currentDays.filter((day) => day !== dayValue)
        : [...currentDays, dayValue].sort((left, right) => left - right);
      return {
        ...prev,
        form: {
          ...prev.form,
          allowed_days: nextDays,
        },
      };
    });
  }, []);

  const handleSaveAccessSchedule = useCallback(async () => {
    const target = accessScheduleDialog.user;
    const form = accessScheduleDialog.form || DEFAULT_ACCESS_SCHEDULE;
    if (!target?.id) return;
    if (form.enabled && (!form.start_time || !form.end_time)) {
      showSnack("Configura hora inicio y hora fin.", "warning");
      return;
    }

    setAccessScheduleDialog((prev) => ({ ...prev, saving: true }));
    try {
      await Accounts.updateUserAccessSchedule(target.id, {
        enabled: Boolean(form.enabled),
        start_time: form.enabled ? form.start_time : null,
        end_time: form.enabled ? form.end_time : null,
        allowed_days: Array.isArray(form.allowed_days) ? form.allowed_days : [],
      });
      showSnack("Horario de acceso guardado.", "success");
      closeAccessSchedule();
    } catch (error) {
      setAccessScheduleDialog((prev) => ({ ...prev, saving: false }));
      showSnack(
        error?.response?.data?.detail || "No se pudo guardar el horario de acceso.",
        "error"
      );
    }
  }, [accessScheduleDialog.form, accessScheduleDialog.user, closeAccessSchedule]);

  useEffect(() => {
    if (!canAssignExtras || !extraPanelOpen || !selectedUser?.id) return;

    (async () => {
      setExtraLoading(true);
      try {
        const data = await RBAC.userCompanies(selectedUser.id);
        const list = toArray(data);
        setExtraAssignments(list);
        const first = list[0];
        const nextCompanyId = first?.company?.id || "";
        setExtraCompanyId(nextCompanyId);
        if (nextCompanyId && Array.isArray(first?.extra_permissions)) {
          setExtraSelectedIds(first.extra_permissions.map((permission) => permission.id));
        } else {
          setExtraSelectedIds([]);
        }
      } catch {
        setExtraAssignments([]);
        setExtraSelectedIds([]);
      } finally {
        setExtraLoading(false);
      }
    })();
  }, [canAssignExtras, extraPanelOpen, selectedUser]);

  useEffect(() => {
    if (!extraCompanyId) return;
    const row = extraAssignments.find(
      (item) => String(item.company?.id || item.company_id) === String(extraCompanyId)
    );
    if (row && Array.isArray(row.extra_permissions)) {
      setExtraSelectedIds(row.extra_permissions.map((permission) => permission.id));
    } else {
      setExtraSelectedIds([]);
    }
  }, [extraAssignments, extraCompanyId]);

  const handleSaveExtras = async () => {
    if (!selectedUser?.id || !extraCompanyId) return;
    const selectedAssignment = extraAssignments.find(
      (item) => String(item.company?.id || item.company_id) === String(extraCompanyId)
    );
    if (!selectedAssignment?.role?.id) return;

    try {
      setExtraLoading(true);
      await RBAC.assignUserCompany({
        user_id: selectedUser.id,
        company_id: extraCompanyId,
        role: selectedAssignment.role.id,
        is_active: selectedAssignment.is_active ?? true,
        current: selectedAssignment.current ?? false,
        extra_permission_ids: extraSelectedIds,
      });
      showSnack("Permisos extra actualizados.", "success");
      const data = await RBAC.userCompanies(selectedUser.id);
      setExtraAssignments(toArray(data));
      await loadUsers();
    } catch {
      showSnack("No se pudieron actualizar los permisos extra.", "error");
    } finally {
      setExtraLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 80 },
      {
        field: "name",
        headerName: "Nombre",
        flex: 1,
        minWidth: 240,
        sortable: false,
        renderCell: ({ row }) => {
          const initials =
            `${row.first_name?.[0] || ""}${row.last_name?.[0] || ""}`.trim().toUpperCase() ||
            row.username?.slice(0, 2).toUpperCase() ||
            "US";

          const hasCurrent = toArray(row.user_companies).some((item) => item.current);

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                height: "100%",
                width: "100%",
                minWidth: 0,
              }}
            >
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                badgeContent={
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: hasCurrent ? "success.main" : "grey.400",
                      border: "1.5px solid #fff",
                    }}
                  />
                }
              >
                <Avatar
                  sx={{
                    bgcolor: "primary.main",
                    width: 32,
                    height: 32,
                    fontSize: "0.8rem",
                  }}
                >
                  {initials}
                </Avatar>
              </Badge>

              <Box
                sx={{
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  lineHeight: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, lineHeight: 1.1 }}
                  noWrap
                >
                  {`${row.first_name || ""} ${row.last_name || ""}`.trim() || row.username}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1.1 }}
                  noWrap
                >
                  @{row.username}
                </Typography>
              </Box>
            </Box>
          );
        },
      },
      {
        field: "email",
        headerName: "Email",
        width: 240,
        renderCell: ({ value }) => (
          <Chip size="small" variant="outlined" label={value || "-"} sx={{ borderRadius: 2 }} />
        ),
      },
      {
        field: "dpi",
        headerName: "DPI",
        width: 170,
        valueGetter: (_, row) => row?.dpi || "-",
      },
      {
        field: "is_active",
        headerName: "Estado",
        width: 120,
        renderCell: ({ value }) => (
          <Chip
            size="small"
            label={value ? "Activo" : "Inactivo"}
            color={value ? "success" : "default"}
            variant={value ? "filled" : "outlined"}
          />
        ),
      },
      {
        field: "last_login",
        headerName: "Ultimo acceso",
        width: 170,
        renderCell: ({ value }) => (
          <Tooltip title={formatAbsolute(value)} arrow>
            <Chip size="small" variant="outlined" label={value ? formatRelative(value) : "Nunca"} />
          </Tooltip>
        ),
      },
      {
        field: "date_joined",
        headerName: "Creado",
        width: 180,
        renderCell: ({ value }) => (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
            {formatDateTime(value)}
          </Typography>
        ),
      },
      {
        field: "companies",
        headerName: "Empresas / Rol",
        flex: 1,
        minWidth: 280,
        sortable: false,
        renderCell: ({ row }) => {
          const companiesList = toArray(row.user_companies);
          if (!companiesList.length) {
            return <Chip size="small" label="Sin asignaciones" variant="outlined" />;
          }
          return (
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
              {companiesList.map((item) => (
                <Chip
                  key={item.id}
                  size="small"
                  label={`${item.company?.name || "Empresa"} - ${item.role?.name || "Sin rol"}${item.current ? " - Activa" : ""}`}
                  color={item.current ? "primary" : "default"}
                  variant={item.current ? "filled" : "outlined"}
                  onClick={() => {
                    if (canAssignUsers) {
                      openAssign(row, item.company?.id);
                    }
                  }}
                  sx={{ borderRadius: 1.5, cursor: canAssignUsers ? "pointer" : "default" }}
                />
              ))}
            </Stack>
          );
        },
      },
      {
        field: "actions",
        headerName: "Acciones",
        width: 170,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.25}>
            <Tooltip title="Editar empresa / rol">
              <span>
                <IconButton
                  size="small"
                  disabled={!canAssignUsers}
                  onClick={() => openAssign(row)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Asignar empresa / rol">
              <span>
                <IconButton
                  size="small"
                  disabled={!canAssignUsers}
                  onClick={() =>
                    openAssign({ ...row, user_companies: row.user_companies || [] }, "")
                  }
                >
                  <LinkIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Horario de acceso">
              <span>
                <IconButton
                  size="small"
                  disabled={!canManageUsers}
                  onClick={() => openAccessSchedule(row)}
                >
                  <AccessTimeIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={row.is_active ? "Desactivar usuario" : "Activar usuario"}>
              <span>
                <IconButton
                  size="small"
                  disabled={!canManageUsers}
                  color={row.is_active ? "error" : "success"}
                  onClick={() => handleToggleUser(row)}
                >
                  {row.is_active ? (
                    <BlockIcon fontSize="small" />
                  ) : (
                    <CheckCircleIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            {!row.is_active ? (
              <Tooltip title="Eliminar permanentemente">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={!canManageUsers}
                    onClick={() => setDeleteDialog({ open: true, user: row, loading: false })}
                  >
                    <DeleteForeverOutlined fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
          </Stack>
        ),
      },
    ],
    [canAssignUsers, canManageUsers, handleToggleUser, openAccessSchedule, openAssign]
  );

  const gridHeight = isMobile ? "calc(100vh - 250px)" : "calc(100vh - 300px)";

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <MainText title={'Usuarios'} subtitle={'Listado de usuarios'} icon={People} width="50%"/>

        <Stack direction="row" spacing={1} alignItems="center">
          {canAssignExtras && (
            <Button
              variant={extraPanelOpen ? "contained" : "outlined"}
              size="small"
              startIcon={<ManageAccountsIcon fontSize="small" />}
              onClick={() => setExtraPanelOpen((prev) => !prev)}
              sx={{ textTransform: "none" }}
            >
              Permisos extra
            </Button>
          )}
          <Tooltip title="Refrescar listado">
            <IconButton onClick={loadUsers}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          placeholder="Buscar usuario..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <DatePick
          name="start_date"
          label="Fecha inicio"
          value={startDate}
          onChange={(_, value) => setStartDate(value)}
          size="small"
          allowHistoricalDates
          allowFutureDates={false}
          minWidth={170}
          maxWidth={210}
        />
        <DatePick
          name="end_date"
          label="Fecha fin"
          value={endDate}
          onChange={(_, value) => setEndDate(value)}
          size="small"
          allowHistoricalDates
          allowFutureDates={false}
          minWidth={170}
          maxWidth={210}
        />

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Empresa</InputLabel>
          <Select
            label="Empresa"
            value={companyId}
            onChange={(event) => setCompanyId(event.target.value)}
          >
            <MenuItem value="">(Todas)</MenuItem>
            {companies.map((company) => (
              <MenuItem key={company.id} value={company.id}>
                {company.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={<Switch checked={activeOnly} onChange={(event) => setActiveOnly(event.target.checked)} />}
          label={activeOnly ? "Activos" : "Inactivos"}
        />
      </Stack>

      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
        <Box sx={{ height: gridHeight, width: extraPanelOpen ? "66%" : "100%" }}>
          <DataGrid
            rows={users}
            columns={columns}
            getRowId={(row) => row.id}
            loading={loading}
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={(model) => {
              setSelectionModel(model);
              const rowId = Array.isArray(model) ? model[0] : null;
              const userRow = users.find((item) => String(item.id) === String(rowId));
              setSelectedUser(userRow || null);
            }}
            disableRowSelectionOnClick={false}
            hideFooterSelectedRowCount
            disableColumnFilter
            disableColumnMenu
            pageSizeOptions={[10, 20, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
            density={isMobile ? "compact" : "standard"}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#757B2B",
                color: "#ffffff",
              },
              "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:hover":
              {
                backgroundColor: "#757B2B !important",
              },
              "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator":
              {
                color: "#ffffff !important",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "rgba(16, 23, 60, 0.08)",
              },
            }}
          />
        </Box>

        {extraPanelOpen && canAssignExtras && (
          <Paper
            elevation={2}
            sx={{
              flex: 1,
              minWidth: 280,
              maxWidth: 390,
              height: gridHeight,
              p: 2,
              borderRadius: 2.5,
              display: "flex",
              flexDirection: "column",
              gap: 1.2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Permisos extra por usuario
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Selecciona un usuario en la tabla y ajusta solo permisos extra.
            </Typography>

            <Box
              sx={{
                p: 1.2,
                borderRadius: 1.5,
                border: "1px dashed",
                borderColor: "divider",
                bgcolor: "grey.50",
              }}
            >
              <Typography variant="body2" color={selectedUser ? "text.primary" : "text.secondary"}>
                {selectedUser
                  ? `${(selectedUser.first_name || "").trim()} ${(selectedUser.last_name || "").trim()} - ${selectedUser.email || selectedUser.username}`
                  : "Selecciona un usuario en la tabla."}
              </Typography>
            </Box>

            <FormControl fullWidth size="small" disabled={!selectedUser}>
              <InputLabel>Empresa</InputLabel>
              <Select
                label="Empresa"
                value={extraCompanyId || ""}
                onChange={(event) => setExtraCompanyId(event.target.value)}
              >
                {extraAssignments.map((assignment) => (
                  <MenuItem
                    key={assignment.id}
                    value={assignment.company?.id || assignment.company_id}
                  >
                    {assignment.company?.name || "Empresa"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: "divider",
                p: 1,
                bgcolor: "background.default",
              }}
            >
              {extraLoading && (
                <Typography variant="body2" color="text.secondary">
                  Cargando...
                </Typography>
              )}

              {!extraLoading && (!selectedUser || !extraCompanyId) && (
                <Typography variant="body2" color="text.secondary">
                  Selecciona usuario y empresa para editar permisos.
                </Typography>
              )}

              {!extraLoading && selectedUser && extraCompanyId && (
                <Stack spacing={0.35}>
                  {availablePerms.map((permission) => {
                    const assignment = extraAssignments.find(
                      (item) =>
                        String(item.company?.id || item.company_id) === String(extraCompanyId)
                    );
                    const baseRolePermissionIds = new Set(
                      toArray(assignment?.role?.permissions).map((item) => item.id)
                    );

                    const isFromRole = baseRolePermissionIds.has(permission.id);
                    const isExtra = extraSelectedIds.includes(permission.id);
                    const checked = isFromRole || isExtra;

                    return (
                      <Box
                        key={permission.id}
                        sx={{
                          px: 1,
                          py: 0.25,
                          borderRadius: 1.2,
                          bgcolor: isFromRole
                            ? "rgba(16, 23, 60, 0.04)"
                            : isExtra
                              ? "rgba(176, 145, 72, 0.08)"
                              : "transparent",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={checked}
                              disabled={isFromRole}
                              onChange={() => {
                                if (isFromRole) return;
                                setExtraSelectedIds((prev) =>
                                  prev.includes(permission.id)
                                    ? prev.filter((id) => id !== permission.id)
                                    : [...prev, permission.id]
                                );
                              }}
                            />
                          }
                          label={
                            <Stack spacing={0} sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                                {permissionFriendlyLabel(permission)}
                              </Typography>
                              {permissionCaption(permission, isSuperuser) ? (
                                <Typography variant="caption" color="text.secondary">
                                  {permissionCaption(permission, isSuperuser)}
                                </Typography>
                              ) : null}
                            </Stack>
                          }
                        />
                        {isFromRole && <Chip size="small" label="Del rol" variant="outlined" />}
                        {!isFromRole && isExtra && (
                          <Chip
                            size="small"
                            label="Extra"
                            sx={{
                              bgcolor: "rgba(176, 145, 72, 0.12)",
                              color: "#6b562b",
                            }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                size="small"
                onClick={() => {
                  setExtraPanelOpen(false);
                  setSelectedUser(null);
                  setSelectionModel([]);
                }}
              >
                Cerrar
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleSaveExtras}
                disabled={extraLoading || !selectedUser || !extraCompanyId}
              >
                Guardar cambios
              </Button>
            </Stack>
          </Paper>
        )}
      </Box>

      <CustomModal
        open={assignOpen}
        setOpen={setAssignOpen}
        title="Asignar/Editar empresa y rol"
        component={AssignCompanyRoleForm}
        componentProps={{
          targetUser,
          companies,
          roles,
          assign,
          setAssign,
          onSubmit: handleAssign,
        }}
        width={isMobile ? "92%" : "520px"}
      />

      <Dialog
        open={accessScheduleDialog.open}
        onClose={closeAccessSchedule}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Horario de acceso</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {accessScheduleDialog.user
                ? `${(accessScheduleDialog.user.first_name || "").trim()} ${(accessScheduleDialog.user.last_name || "").trim()}`.trim() ||
                  accessScheduleDialog.user.username
                : "-"}
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(accessScheduleDialog.form.enabled)}
                  disabled={accessScheduleDialog.loading || accessScheduleDialog.saving}
                  onChange={(event) => updateAccessScheduleForm({ enabled: event.target.checked })}
                />
              }
              label="Restringir acceso"
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                fullWidth
                size="small"
                type="time"
                label="Hora inicio"
                value={accessScheduleDialog.form.start_time || ""}
                disabled={!accessScheduleDialog.form.enabled || accessScheduleDialog.loading || accessScheduleDialog.saving}
                onChange={(event) => updateAccessScheduleForm({ start_time: event.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                size="small"
                type="time"
                label="Hora fin"
                value={accessScheduleDialog.form.end_time || ""}
                disabled={!accessScheduleDialog.form.enabled || accessScheduleDialog.loading || accessScheduleDialog.saving}
                onChange={(event) => updateAccessScheduleForm({ end_time: event.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.8 }}>
                Dias permitidos (opcional)
              </Typography>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                {WEEKDAYS.map((day) => {
                  const selected = Array.isArray(accessScheduleDialog.form.allowed_days) &&
                    accessScheduleDialog.form.allowed_days.includes(day.value);
                  return (
                    <Chip
                      key={day.value}
                      label={day.label}
                      clickable={Boolean(accessScheduleDialog.form.enabled)}
                      color={selected ? "primary" : "default"}
                      variant={selected ? "filled" : "outlined"}
                      onClick={() => {
                        if (!accessScheduleDialog.form.enabled || accessScheduleDialog.loading || accessScheduleDialog.saving) return;
                        toggleAccessScheduleDay(day.value);
                      }}
                    />
                  );
                })}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Sin dias seleccionados permite todos los dias.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAccessSchedule} disabled={accessScheduleDialog.saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={accessScheduleDialog.loading || accessScheduleDialog.saving || !accessScheduleDialog.user?.id}
            onClick={handleSaveAccessSchedule}
          >
            {accessScheduleDialog.saving ? "Guardando..." : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialog.open}
        onClose={() => {
          if (deleteDialog.loading) return;
          setDeleteDialog({ open: false, user: null, loading: false });
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Eliminar usuario</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Esta accion eliminara permanentemente al usuario{" "}
            <strong>
              {deleteDialog.user
                ? `${deleteDialog.user.first_name || ""} ${deleteDialog.user.last_name || ""}`.trim() ||
                  deleteDialog.user.username
                : "-"}
            </strong>.
            Solo se permite porque el usuario ya esta inactivo.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, user: null, loading: false })}
            disabled={deleteDialog.loading}
          >
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteDialog.loading || !deleteDialog.user?.id}
            onClick={handleDeleteUser}
          >
            {deleteDialog.loading ? "Eliminando..." : "Eliminar permanentemente"}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />
    </Box>
  );
}
